export const PRODUCT_SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;
export const PRODUCT_SCREENSHOT_ACCEPTED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

export type ScreenshotFileMetadata = {
  name?: string;
  type?: string;
  size?: number;
};

export type ProductScreenshotUploadResult = {
  url: string;
  storageKey: string;
  width: number;
  height: number;
  format: 'webp';
};

export function validateScreenshotFile(file: ScreenshotFileMetadata) {
  if (!PRODUCT_SCREENSHOT_ACCEPTED_TYPES.has(String(file.type || '').toLocaleLowerCase())) {
    return 'Choose a PNG, JPEG or WebP image.';
  }
  if (!Number.isFinite(file.size) || Number(file.size) <= 0) return 'The selected image is empty.';
  if (Number(file.size) > PRODUCT_SCREENSHOT_MAX_BYTES) return 'The screenshot must be 10 MB or smaller.';
  return null;
}

export function normalizeScreenshotProcessorResult(value: unknown, publicBaseUrl: string): ProductScreenshotUploadResult | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.format !== 'webp') return null;
  if (typeof raw.url !== 'string' || typeof raw.storageKey !== 'string') return null;
  const width = Number(raw.width);
  const height = Number(raw.height);
  if (!Number.isInteger(width) || width <= 0 || width > 20000) return null;
  if (!Number.isInteger(height) || height <= 0 || height > 20000) return null;
  if (!raw.storageKey.trim() || raw.storageKey.length > 1024) return null;

  try {
    const url = new URL(raw.url);
    const base = new URL(publicBaseUrl);
    if (url.protocol !== 'https:' || url.origin !== base.origin) return null;
    if (!url.pathname.toLocaleLowerCase().endsWith('.webp')) return null;
    return { url: url.toString(), storageKey: raw.storageKey, width, height, format: 'webp' };
  } catch {
    return null;
  }
}

export async function uploadSubmissionScreenshot(submitId: number, file: File) {
  const formData = new FormData();
  formData.append('submitId', String(submitId));
  formData.append('file', file);
  const response = await fetch('/api/submit/screenshot', { method: 'POST', body: formData });
  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await response.json() : null;
  if (!response.ok) throw new Error(body?.error || 'The screenshot could not be processed.');
  return body as ProductScreenshotUploadResult;
}
